// Extracted from pages/Articles/CreateArticle.js (behavior-preserving move).
// Functions are recreated on every render with the latest wizard state, exactly as
// they were when they lived inside the page component. `log` is the original no-op.
import { useCallback } from 'react';
import { directusAPI } from '../../../api';
import { saveArticleToDirectus } from '../../../pages/Articles/saveArticle';
import { getStoredToken, log } from '../services/articleGenerationApi';

/**
 * Bulk mode: creates N article records and puts them straight into the worker queue.
 */
export function useBulkGeneration(ctx) {
  const {
    numberOfArticles,
    bulkTopic,
    contentType,
    toneOfVoice,
    complexity,
    language,
    selectedProject,
    includeImages,
    thumbnailSource,
    keywords,
    sources,
    setIsGenerationCompleted,
    setGeneratedArticleId,
    setSaveError,
  } = ctx;

  // Function to handle bulk article generation
  const handleBulkGeneration = useCallback(async () => {
    try {
      log('\n🚀 ============ BULK ARTICLES GENERATION START ============');
      log(`📋 BULK: Starting bulk generation for ${numberOfArticles} articles`);
      log('🎯 BULK: Topic:', bulkTopic);

      // Skip generating modal for bulk - everything happens in background
      log('🎯 BULK: Starting background bulk generation (no loading modal)');

      try {
          const createdArticles = [];

          // Create all articles first
          log(`📋 BULK: Creating ${numberOfArticles} article records`);

          for (let i = 1; i <= numberOfArticles; i++) {
            log(`📋 BULK: Creating article ${i}/${numberOfArticles}`);

            // Create article record for bulk generation
            const articleData = {
              title: `${bulkTopic} - Article ${i}`,
              article_context: `${bulkTopic} (Bulk Generation ${i}/${numberOfArticles})`,
              content_type: contentType || 'blogpost',
              tone_of_voice: toneOfVoice || 'casual',
              fog_index: complexity || 'fog index: 15',
              language: language || 'polski',
              project: selectedProject || null,
              status: 'draft',
              article_thumbnail_included: includeImages !== false,
              article_thumbnail_source: includeImages ? thumbnailSource : null,
              keywords: keywords || [],
              sources: sources || []
            };

            const newArticle = await saveArticleToDirectus(articleData);

            createdArticles.push(newArticle.data);
            log(`✅ BULK: Article ${i} created with ID:`, newArticle.data.id);
          }

          // Set all articles to "ongoing" and add to queue
          log('🔒 BULK: Setting all articles to ongoing and adding to queue');

          for (const article of createdArticles) {
            try {
              await directusAPI.patch(`/items/articles/${article.id}`, {
                status: 'ongoing',
                queue_status: 'queued'
              }, {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${getStoredToken()}`
                }
              });
              log(`✅ BULK: Article ${article.id} added to queue`);
            } catch (error) {
              log(`❌ BULK: Error queuing article ${article.id}:`, error);
            }
          }

          // Show success modal after all articles are created and queued
          log('🎉 BULK: All articles created and queued successfully');
          log('🎯 BULK: Showing success modal');

          setIsGenerationCompleted(true);
          setGeneratedArticleId(createdArticles[0].id); // Use first article ID for navigation

        } catch (error) {
          log('❌ BULK: Error during bulk generation:', error);
          setSaveError('Error creating bulk articles: ' + error.message);
        }

    } catch (error) {
      log('❌ BULK: Error in handleBulkGeneration:', error);
      setSaveError('Error starting bulk generation: ' + error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- thumbnailSource stable default; omit to avoid bulk handler churn
  }, [numberOfArticles, bulkTopic, contentType, toneOfVoice, complexity, language, selectedProject, includeImages, keywords, sources, thumbnailSource]);

  return handleBulkGeneration;
}
